import { haversineKm, haversineMeters } from './gis';

// NWS_Z reflectivity palette — mirrors radar_backend/rendering/palettes.py
// Format: [lo_dBZ, [R, G, B]] using the rendered lo-boundary colour for each bin.
const NWS_Z = [
  [ 5, [ 48, 163, 218]],
  [10, [ 48, 163, 218]],
  [15, [ 20,  90, 200]],
  [20, [ 50, 215,  50]],
  [25, [ 20, 190,  20]],
  [30, [  0, 155,   0]],
  [35, [240, 230,  20]],
  [40, [230, 155,   0]],
  [45, [230,  70,   0]],
  [50, [210,   0,   0]],
  [55, [240,  20,  20]],
  [60, [255,   0,   0]],
  [65, [220,   0, 210]],
  [70, [160,  60, 190]],
  [75, [255, 255, 255]],
];

// Threshold raised to 12000 (was 9000) so Mapbox contrast/saturation shifts
// don't cause spurious nulls over valid radar echoes.
const DBZ_MATCH_THRESHOLD = 12_000;

let _off     = null; // 1×1 offscreen canvas for single-pixel desktop reads
let _areaOff = null; // NxN offscreen canvas for batched mobile area reads

/**
 * Sample the rendered dBZ value at screen pixel (px, py) from the Mapbox
 * WebGL canvas.  Requires preserveDrawingBuffer: true on the map.
 * Returns the lo-bound dBZ integer, or null if no radar pixel there.
 */
export function sampleDbzFromCanvas(map, px, py) {
  const webgl = map.getCanvas();
  if (!_off) {
    _off = document.createElement('canvas');
    _off.width  = 1;
    _off.height = 1;
  }
  const ctx = _off.getContext('2d', { willReadFrequently: true });
  const dpr = window.devicePixelRatio || 1;
  try {
    ctx.drawImage(webgl, Math.round(px * dpr), Math.round(py * dpr), 1, 1, 0, 0, 1, 1);
  } catch {
    return null;
  }
  const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
  if (a < 30) return null;
  let best = null, bestDist = Infinity;
  for (const [dbz, [pr, pg, pb]] of NWS_Z) {
    const d = (r - pr) ** 2 + (g - pg) ** 2 + (b - pb) ** 2;
    if (d < bestDist) { bestDist = d; best = dbz; }
  }
  return bestDist < DBZ_MATCH_THRESHOLD ? best : null;
}

/**
 * Batched area sample: reads a single NxN physical-pixel region from the
 * WebGL canvas (one drawImage + one getImageData) then scans all pixels in JS.
 *
 * Strategy:
 *  1. Check the exact centre physical pixel — if it has a valid radar colour,
 *     return it immediately (highest fidelity, "what the crosshair tip points at").
 *  2. Otherwise search a ±radiusCss CSS-pixel fallback ring and return the
 *     MAXIMUM dBZ found (peak reflectivity near centre, handles tile gaps).
 *
 * radiusCss = 2 → 5×5 CSS px area, ~15×15 physical on 3× screens.
 */
export function sampleDbzAreaFromCanvas(map, cssCx, cssCy, radiusCss = 2) {
  const webgl   = map.getCanvas();
  const dpr     = window.devicePixelRatio || 1;
  const pradius = Math.ceil(radiusCss * dpr);
  const side    = pradius * 2 + 1; // physical pixel side length

  if (!_areaOff || _areaOff.width !== side) {
    _areaOff        = document.createElement('canvas');
    _areaOff.width  = side;
    _areaOff.height = side;
  }

  const ctx  = _areaOff.getContext('2d', { willReadFrequently: true });
  ctx.imageSmoothingEnabled = false;

  // Physical pixel centre of the crosshair, clamped to canvas bounds
  const pcx = Math.round(cssCx * dpr);
  const pcy = Math.round(cssCy * dpr);
  const sx  = Math.max(0, Math.min(webgl.width  - side, pcx - pradius));
  const sy  = Math.max(0, Math.min(webgl.height - side, pcy - pradius));

  try {
    ctx.drawImage(webgl, sx, sy, side, side, 0, 0, side, side);
  } catch {
    return null;
  }

  const data = ctx.getImageData(0, 0, side, side).data;
  const ocx  = pcx - sx; // centre column within offscreen canvas
  const ocy  = pcy - sy; // centre row

  function matchPixel(col, row) {
    const i = (row * side + col) * 4;
    if (data[i + 3] < 30) return null;
    const r = data[i], g = data[i + 1], b = data[i + 2];
    let best = null, bestD = Infinity;
    for (const [dbz, [pr, pg, pb]] of NWS_Z) {
      const d = (r - pr) ** 2 + (g - pg) ** 2 + (b - pb) ** 2;
      if (d < bestD) { bestD = d; best = dbz; }
    }
    return bestD < DBZ_MATCH_THRESHOLD ? best : null;
  }

  // 1. Exact centre pixel — return immediately if it matches
  const centreDbz = matchPixel(ocx, ocy);
  if (centreDbz !== null) return centreDbz;

  // 2. Fallback: scan the whole area, return the MAXIMUM dBZ found
  //    (handles tile-edge gaps where the exact centre is transparent)
  let maxDbz = null;
  for (let row = 0; row < side; row++) {
    for (let col = 0; col < side; col++) {
      if (row === ocy && col === ocx) continue; // already checked
      const v = matchPixel(col, row);
      if (v !== null && (maxDbz === null || v > maxDbz)) maxDbz = v;
    }
  }
  return maxDbz;
}

/** Beam height (km AGL) using the 4/3 Earth-radius model. */
export function beamHeightKm(distKm, elevDeg = 0.5, antennaKm = 0.05) {
  const Re = 8495; // 4/3 × 6371 km effective Earth radius
  const θ  = (elevDeg * Math.PI) / 180;
  return Math.sqrt(distKm ** 2 + Re ** 2 + 2 * distKm * Re * Math.sin(θ)) - Re + antennaKm;
}

/** True bearing (0–360°) from radar site to probe location. */
export function getBearing(siteLng, siteLat, probeLng, probeLat) {
  const φ1 = (siteLat  * Math.PI) / 180;
  const φ2 = (probeLat * Math.PI) / 180;
  const Δλ = ((probeLng - siteLng) * Math.PI) / 180;
  const y  = Math.sin(Δλ) * Math.cos(φ2);
  const x  = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

const DIRS = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];
export function bearingToCardinal(deg) {
  return DIRS[Math.round(deg / 22.5) % 16];
}

/**
 * Find the nearest METAR station within maxDistM metres of the probe.
 * Returns the GeoJSON Feature, or null if none within range.
 * Pure function — hysteresis is handled by the caller.
 */
export function findNearestMetar(metarData, probeLng, probeLat, maxDistM = 200_000) {
  if (!metarData?.features?.length) return null;
  let nearest = null, nearestDist = Infinity;
  for (const f of metarData.features) {
    // GeoJSON coordinates are [lng, lat]
    const [fLng, fLat] = f.geometry.coordinates;
    const d = haversineMeters([probeLng, probeLat], [fLng, fLat]);
    if (d < nearestDist) { nearestDist = d; nearest = f; }
  }
  return nearestDist <= maxDistM ? nearest : null;
}

/**
 * Format a distance (given in km) using the specified unit string.
 * Works for both horizontal radar distance and beam height AGL.
 * Units: 'km' | 'mi' | 'nm' | 'ft' | 'yd'
 */
export function formatProbeDistance(distKm, unit = 'km') {
  if (distKm == null) return null;
  switch (unit) {
    case 'mi': {
      const mi = distKm * 0.621371;
      return `${mi < 100 ? mi.toFixed(1) : Math.round(mi)} mi`;
    }
    case 'nm': {
      const nm = distKm * 0.539957;
      return `${nm < 100 ? nm.toFixed(1) : Math.round(nm)} nm`;
    }
    case 'ft': {
      const ft = Math.round(distKm * 3280.84);
      return ft >= 10000 ? `${(ft / 1000).toFixed(1)}k ft` : `${ft.toLocaleString()} ft`;
    }
    case 'yd': {
      const yd = Math.round(distKm * 1093.61);
      return yd >= 10000 ? `${(yd / 1000).toFixed(1)}k yd` : `${yd.toLocaleString()} yd`;
    }
    default:
      return `${distKm < 100 ? distKm.toFixed(1) : Math.round(distKm)} km`;
  }
}

// ── Tile-direct dBZ sampling ─────────────────────────────────────────────────
// Fetches the raw radar PNG tile for a given lng/lat and samples its pixel
// directly — bypasses the Mapbox composited canvas so road/shield colours
// can never be mistaken for radar echoes.

const _tileCache    = new Map(); // tileUrl → 2d ctx (offscreen canvas)
const _tileInflight = new Map(); // tileUrl → Promise<ctx|null>  (dedup)
const TILE_CACHE_MAX = 32;

function _evictTile() {
  if (_tileCache.size >= TILE_CACHE_MAX)
    _tileCache.delete(_tileCache.keys().next().value);
}

function _tileXY(lng, lat, z) {
  const n  = 1 << z;
  const tx = Math.floor((lng + 180) / 360 * n);
  const φ  = lat * Math.PI / 180;
  const ty = Math.floor((1 - Math.log(Math.tan(φ) + 1 / Math.cos(φ)) / Math.PI) / 2 * n);
  return { tx, ty };
}

function _pixelInTile(lng, lat, z, tileSize) {
  const n  = 1 << z;
  const { tx, ty } = _tileXY(lng, lat, z);
  const φ  = lat * Math.PI / 180;
  const fx = (lng + 180) / 360 * n - tx;
  const fy = (1 - Math.log(Math.tan(φ) + 1 / Math.cos(φ)) / Math.PI) / 2 * n - ty;
  return { px: Math.floor(fx * tileSize), py: Math.floor(fy * tileSize) };
}

async function _fetchTileCtx(url) {
  if (_tileCache.has(url))    return _tileCache.get(url);
  if (_tileInflight.has(url)) return _tileInflight.get(url);
  const p = (async () => {
    try {
      const resp = await fetch(url, { credentials: 'include' });
      if (!resp.ok) return null;
      const bmp = await createImageBitmap(await resp.blob());
      const c   = document.createElement('canvas');
      c.width = bmp.width; c.height = bmp.height;
      const ctx = c.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(bmp, 0, 0);
      bmp.close();
      _evictTile();
      _tileCache.set(url, ctx);
      return ctx;
    } catch { return null; }
    finally  { _tileInflight.delete(url); }
  })();
  _tileInflight.set(url, p);
  return p;
}

/**
 * Sample dBZ at (lng, lat) from the raw radar tile PNG — no basemap blending.
 * tileUrlTemplate must be the absolute URL for the currently-displayed frame
 * (caller owns which frame is active; do NOT derive from map sources, which
 * may include stale off-screen buffers from dual-buffer NEXRAD playback).
 * Returns Promise<number|null>.
 */
export async function sampleDbzFromTileAsync(tileUrlTemplate, lng, lat) {
  const tmpl = tileUrlTemplate;
  if (!tmpl) return null;
  const z          = 8; // radar maxzoom — highest resolution tile
  const { tx, ty } = _tileXY(lng, lat, z);
  const tileSize   = 512;
  const { px, py } = _pixelInTile(lng, lat, z, tileSize);
  const url = tmpl.replace('{z}', z).replace('{x}', tx).replace('{y}', ty);
  const ctx = await _fetchTileCtx(url);
  if (!ctx) return null;
  const w = ctx.canvas.width, h = ctx.canvas.height;
  const [r, g, b, a] = ctx.getImageData(
    Math.max(0, Math.min(w - 1, px)),
    Math.max(0, Math.min(h - 1, py)),
    1, 1,
  ).data;
  if (a < 30) return null;
  let best = null, bestD = Infinity;
  for (const [dbz, [pr, pg, pb]] of NWS_Z) {
    const d = (r - pr) ** 2 + (g - pg) ** 2 + (b - pb) ** 2;
    if (d < bestD) { bestD = d; best = dbz; }
  }
  return bestD < DBZ_MATCH_THRESHOLD ? best : null;
}

export { haversineKm, haversineMeters };
